/**
 * Module: screens/pantene/_uploadXHR
 * Role: XHR-based file upload with progress tracking for the Pantene video
 *       upload screen. fetch() lacks upload progress events; XHR provides
 *       xhr.upload.onprogress.
 *
 *       FTR-1295: public uploadXHR now wraps a single-shot transport with an
 *       auto-retry loop for transient network errors (xhr.onerror). HTTP
 *       status errors and aborts are NEVER retried. All consumers
 *       (useFileUpload, VideoUpload, every brick) inherit retry transparently.
 *
 * Uses: (none — pure browser API)
 * Used by: screens/pantene/VideoUpload.tsx, hooks/useFileUpload.ts
 */

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface UploadXHROptions {
  url: string;
  file: File;
  onProgress: (progress: UploadProgress) => void;
  signal?: AbortSignal;
  /**
   * FTR-1295: retry tuning for transient network errors only. Defaults match
   * AWS full-jitter guidance for interactive uploads. Consumers omit these;
   * they exist mainly so tests can drive the loop deterministically.
   */
  maxAttempts?: number;
  baseDelayMs?: number;
  capDelayMs?: number;
  /** FTR-1317: client timeout in ms. 0 or omitted = no timeout (today's default). */
  timeoutMs?: number;
}

// AWS full-jitter defaults (research-external.md): interactive upload over LTE.
const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BASE_DELAY_MS = 500;
const DEFAULT_CAP_DELAY_MS = 3000;

/**
 * Transient network failure (xhr.onerror) — the ONLY error class that is
 * retried. HTTP status errors and aborts use other types and are never retried.
 * Keeps the legacy message so the user-facing surface is unchanged.
 */
export class UploadNetworkError extends Error {
  constructor() {
    super('PUT failed: network error');
    this.name = 'UploadNetworkError';
  }
}

/**
 * Upload timeout (xhr.timeout fired). NOT retried — distinct from UploadNetworkError.
 * FTR-1317: callers set timeoutMs to enable; default 0 = unset = today's behaviour
 * (money-path Pantene video unchanged).
 */
export class UploadTimeoutError extends Error {
  constructor() {
    super('PUT failed: upload timeout');
    this.name = 'UploadTimeoutError';
  }
}

/**
 * Sleep for `ms`, but reject immediately with AbortError if `signal` aborts
 * during the wait. Used between retry attempts so abort is honoured mid-backoff.
 */
function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      cleanup();
      reject(new DOMException('Upload aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Single PUT of a file to a presigned URL via XHR with progress events.
 * Resolves on HTTP 2xx. Rejects with:
 *   - UploadNetworkError              on transport failure (xhr.onerror)
 *   - Error('PUT failed: HTTP <n>')   on non-2xx status
 *   - DOMException(AbortError)        on abort
 */
function uploadXHROnce({ url, file, onProgress, signal, timeoutMs }: UploadXHROptions): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (e.lengthComputable) {
        onProgress({ loaded: e.loaded, total: e.total });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`PUT failed: HTTP ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new UploadNetworkError());
    };

    xhr.onabort = () => {
      reject(new DOMException('Upload aborted', 'AbortError'));
    };

    if (timeoutMs) {
      xhr.timeout = timeoutMs;
    }

    xhr.ontimeout = () => {
      reject(new UploadTimeoutError());
    };

    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

/**
 * PUT a file to a presigned URL with auto-retry on transient network errors.
 *
 * Retry policy (FTR-1295):
 *   - Only UploadNetworkError (xhr.onerror) is retried.
 *   - HTTP status errors (expired presign, 4xx/5xx) are thrown immediately.
 *   - AbortError is thrown immediately, including if abort fires during the
 *     inter-attempt backoff pause.
 *   - Backoff: full jitter, delay = random(0, min(cap, base * 2^(attempt-1))).
 */
export function uploadXHR(options: UploadXHROptions): Promise<void> {
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const baseDelayMs = options.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
  const capDelayMs = options.capDelayMs ?? DEFAULT_CAP_DELAY_MS;

  const run = async (): Promise<void> => {
    for (let attempt = 1; ; attempt++) {
      try {
        // Reset progress at the start of each attempt so a partial upload from
        // a failed try does not leave the bar stuck.
        options.onProgress({ loaded: 0, total: options.file.size });
        await uploadXHROnce(options);
        return;
      } catch (err) {
        const isRetryable = err instanceof UploadNetworkError;
        const hasAttemptsLeft = attempt < maxAttempts;
        if (!isRetryable || !hasAttemptsLeft || options.signal?.aborted) {
          throw err;
        }
        const expo = Math.min(capDelayMs, baseDelayMs * 2 ** (attempt - 1));
        const delay = Math.random() * expo; // full jitter
        await abortableDelay(delay, options.signal);
      }
    }
  };

  return run();
}
